import { useState, useEffect } from 'react';
import { useSpuIdFromUrl } from '../areas/citizen/hooks/use-spu-id-from-url';
import { TemplateListItemViewModel } from '../api-service/clients';
import { TemplatesApiService } from '../areas/citizen/common/communication/api';
import { INotifierMessage } from '../components/notifier/interfaces';
import { moduleClientFactory } from '../api-service/module-client-factory';
import { useCommonCitizenContext } from '../areas/citizen/citizen-context-hooks';

export interface UseGetDocumentTemplatesResult {
  documentTemplates: TemplateListItemViewModel[];
  isLoading: boolean;
  apiError: INotifierMessage | undefined;
}

export type UseGetDocumentTemplatesOptions = { caseId?: string };

export interface TemplateOption {
  codeName?: string | undefined;
  label?: string | undefined;
  name?: string | undefined;
  value?: { templateId: number; variantId?: number | undefined } | undefined;
}

function mapToTemplateOption(template: TemplateListItemViewModel): TemplateOption {
  return {
    codeName: template.codeName,
    label: template.label,
    name: template.name,
    value: template.value,
  };
}

async function fetchAuthorityTemplates(caseId: string, citizenId: string): Promise<TemplateOption[]> {
  const journalDocumentClient = moduleClientFactory.createCitizenJournalDocumentClient();
  const response = await journalDocumentClient.getDocumentTemplates({
    caseId,
    citizenId,
  });
  return response.templates?.map(mapToTemplateOption) ?? [];
}

async function fetchServiceProviderDocumentTemplates(spuId: string): Promise<TemplateOption[]> {
  const templatesApiService = new TemplatesApiService();
  const response = await templatesApiService.getServiceProviderDocumentTemplateTypes({ serviceProviderId: spuId });
  return response.map(mapToTemplateOption) ?? [];
}

export function useGetDocumentTemplates(options: UseGetDocumentTemplatesOptions): UseGetDocumentTemplatesResult {
  const { caseId } = options;
  const { citizenId } = useCommonCitizenContext();
  const spuId = useSpuIdFromUrl();
  const [documentTemplates, setDocumentTemplates] = useState<TemplateOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<INotifierMessage>();

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setIsLoading(true);

        let templates;

        if (caseId) {
          templates = await fetchAuthorityTemplates(caseId, citizenId);
        } else if (spuId) {
          templates = await fetchServiceProviderDocumentTemplates(spuId);
        }

        setDocumentTemplates(templates ?? []);
      } catch {
        const message: INotifierMessage = {
          id: '-1',
          message: 'Listen af tilgængelige skabeloner kunne ikke hentes. Genindlæs eventuelt siden.',
          severity: 'error',
        };
        setApiError(message);
      } finally {
        setIsLoading(false);
      }
    };
    loadTemplates();
  }, [caseId, citizenId]);

  return {
    documentTemplates,
    isLoading,
    apiError,
  };
}
